import { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import type { PortfolioData } from '../domain/portfolio.types';

export function Contact({ data }: { data: PortfolioData }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', msg: '' });
  
  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.msg) {
      setError('Please fill out all fields.');
      return;
    }

    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      setError('Please complete the reCAPTCHA validation.');
      return;
    }

    // Check if env variables are set
    if (!import.meta.env.VITE_EMAILJS_SERVICE_ID || !import.meta.env.VITE_EMAILJS_TEMPLATE_ID || !import.meta.env.VITE_EMAILJS_PUBLIC_KEY) {
      setError('EmailJS configuration is missing in .env file.');
      return;
    }

    setSending(true);
    setError(null);

    try {
      await emailjs.sendForm(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        formRef.current!,
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      );

      setSent(true);
      setForm({ name: '', email: '', msg: '' });
      recaptchaRef.current?.reset();
      
      setTimeout(() => { setSent(false); }, 4000);
    } catch (err) {
      console.error('EmailJS Error:', err);
      setError('Transmission failed. Please try again or use direct email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section" id="contact">
      <div className="container">
        <div className="reveal" style={{ marginBottom: 56 }}>
          <div className="section-tag">08 · Contact</div>
        </div>
        <div className="contact-shell reveal">
          <div className="contact-grid">
            <div>
              <h2 className="contact-title">
                Let's build<br/>
                <span className="glitch">something sharp.</span>
              </h2>
              <p className="contact-blurb">
                One project at a time. I respond to every honest inquiry within 48 hours — usually with a question or three.
              </p>
              <ul className="contact-list">
                <li><span className="k">EMAIL</span><a href={`mailto:${data.contact.email}`}><span className="v">{data.contact.email}</span></a></li>
                <li><span className="k">CITY</span><span className="v">{data.contact.location}</span></li>
                <li><span className="k">TZ</span><span className="v">{data.contact.timezone}</span></li>
                {data.contact.socials.map((s, i) => (
                  <li key={i}><span className="k">{s.k}</span><a href={s.url} target="_blank" rel="noopener noreferrer"><span className="v">{s.v}</span></a></li>
                ))}
              </ul>
            </div>
            <form ref={formRef} className="contact-form" onSubmit={submit}>
              <div className="contact-field">
                <label>Name</label>
                <input name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" disabled={sending} />
              </div>
              <div className="contact-field">
                <label>Email</label>
                <input name="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@studio.com" disabled={sending} />
              </div>
              <div className="contact-field">
                <label>Brief</label>
                <textarea name="message" value={form.msg} onChange={(e) => setForm({ ...form, msg: e.target.value })} placeholder="What are we building?" disabled={sending} />
              </div>
              
              <div className="contact-field" style={{ marginBottom: '16px' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || 'dummy-key-to-render'}
                  theme="dark"
                  onChange={() => setError(null)}
                />
              </div>

              {error && <div style={{ color: 'var(--danger)', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}
              
              <button className="btn btn-primary" type="submit" style={{ alignSelf: 'flex-start' }} disabled={sending}>
                {sending ? 'Sending...' : sent ? '✓ Transmission sent' : 'Send transmission →'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
